import { useState, useEffect } from 'react';
import { Search, Menu, X, ChevronDown } from 'lucide-react';
import type { Producto, Embajador } from '@/types';

type Page = 'inicio' | 'embajador' | 'catalogo' | 'tratamientos';

interface HeaderProps {
  onSearchClick: () => void;
  currentPage: Page;
  onNavigate: (page: Page, params?: { linea?: string; producto?: Producto }) => void;
  ambassador?: Embajador | null;
}

const navLinks: { name: string; path: Page; submenu?: { name: string; linea: string }[] }[] = [
  { name: 'Inicio', path: 'inicio' },
  { name: 'Embajador', path: 'embajador' },
  { 
    name: 'Catálogo', 
    path: 'catalogo',
    submenu: [
      { name: 'Ver todo', linea: 'todos' },
      { name: 'FT-ELYM LINE', linea: 'FT-ELYM LINE' },
      { name: 'BIENESTAR LINE', linea: 'BIENESTAR LINE' },
      { name: 'AVANCELY LINE', linea: 'AVANCELY LINE' },
      { name: 'VYBES LINE', linea: 'VYBES LINE' },
    ]
  },
  { name: 'Tratamientos', path: 'tratamientos' },
];

export default function Header({ onSearchClick, currentPage, onNavigate }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<string | null>('Catálogo');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [leaveTimer, setLeaveTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigate = (path: Page, params?: { linea?: string; producto?: Producto }) => {
    onNavigate(path, params);
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const handleMouseEnter = (linkName: string) => {
    if (leaveTimer) {
      clearTimeout(leaveTimer);
    }
    setActiveDropdown(linkName);
  };

  const handleMouseLeave = () => {
    const timer = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
    setLeaveTimer(timer);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'py-2 px-2 md:px-8'
            : 'py-4 section-padding'
        }`}
      >
        <div
          className={`mx-auto transition-all duration-500 ${
            isScrolled
              ? 'max-w-6xl bg-white/90 backdrop-blur-xl shadow-lg shadow-black/5 rounded-full px-4 md:px-6 py-2'
              : 'max-w-7xl bg-transparent py-2'
          }`}
        >
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button 
              onClick={() => handleNavigate('inicio')}
              className="flex items-center gap-2 group"
            >
              <img 
                src="/images/Logo_Volando_Alto_Header.webp" 
                alt="Volando Alto Logo" 
                width={160}
                height={59}
                className={`${isScrolled ? 'w-32' : 'w-40'} h-auto transition-all duration-300 group-hover:scale-110`}
              />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => link.submenu && handleMouseEnter(link.name)}
                  onMouseLeave={() => link.submenu && handleMouseLeave()}
                >
                  <button
                    onClick={() => !link.submenu && handleNavigate(link.path)}
                    className={`flex items-center gap-1 text-sm font-medium transition-all duration-300 relative group ${
                      currentPage === link.path
                        ? 'text-[#e91e8c]'
                        : isScrolled
                        ? 'text-gray-700 hover:text-[#e91e8c]'
                        : 'text-gray-700 hover:text-[#e91e8c]'
                    }`}
                  >
                    <span>{link.name}</span>
                    {link.submenu && (
                      <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${
                        activeDropdown === link.name ? 'rotate-180' : ''
                      }`} />
                    )}
                    <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#e91e8c] transition-all duration-300 ${
                      currentPage === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                    }`} />
                  </button>

                  {/* Dropdown Menu */}
                  {link.submenu && activeDropdown === link.name && (
                    <div 
                      className="absolute top-full left-0 mt-[5px] w-56 bg-white rounded-xl shadow-xl shadow-black/10 overflow-hidden animate-fade-in"
                      onMouseEnter={() => handleMouseEnter(link.name)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="py-2">
                        {link.submenu.map((subItem) => (
                          <button
                            key={subItem.name}
                            onClick={() => handleNavigate('catalogo', { linea: subItem.linea })}
                            className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-[#f8f9f5] hover:text-[#e91e8c] transition-colors duration-200"
                          >
                            {subItem.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              <button
                onClick={onSearchClick}
                className={`p-2 rounded-full transition-all duration-300 ${
                  isScrolled
                    ? 'hover:bg-[#f8f9f5] text-gray-700 hover:text-[#e91e8c]'
                    : 'hover:bg-white/50 text-gray-700 hover:text-[#e91e8c]'
                }`}
                aria-label="Buscar"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden p-2 rounded-full transition-all duration-300 ${
                  isScrolled
                    ? 'hover:bg-[#f8f9f5] text-gray-700'
                    : 'hover:bg-white/50 text-gray-700'
                }`}
                aria-label="Menú"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
          isMobileMenuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/20 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        <div
          className={`absolute ${isScrolled ? 'top-20' : 'top-[85px]'} left-4 right-4 bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-500 ${
            isMobileMenuOpen
              ? 'translate-y-0 opacity-100'
              : '-translate-y-8 opacity-0'
          }`}
        >
          <nav className="p-4">
            {navLinks.map((link) => (
              <div key={link.name}>
                <button
                  onClick={() => {
                    if (link.submenu) {
                      setMobileSubmenuOpen(mobileSubmenuOpen === link.name ? null : link.name);
                    } else {
                      handleNavigate(link.path);
                    }
                  }}
                  className={`flex items-center justify-between w-full text-left px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 ${
                    currentPage === link.path
                      ? 'bg-[#f8f9f5] text-[#e91e8c]'
                      : 'text-gray-700 hover:bg-[#f8f9f5] hover:text-[#e91e8c]'
                  }`}
                >
                  <span>{link.name}</span>
                  {link.submenu && (
                    <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${
                      mobileSubmenuOpen === link.name ? 'rotate-180' : ''
                    }`} />
                  )}
                </button>
                {link.submenu && mobileSubmenuOpen === link.name && (
                  <div className="ml-4 mt-1 space-y-1 animate-fade-in">
                    {link.submenu.map((subItem) => (
                      <button
                        key={subItem.name}
                        onClick={() => handleNavigate('catalogo', { linea: subItem.linea })}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-500 hover:text-[#e91e8c] transition-colors duration-200"
                      >
                        {subItem.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
}
